import React, { useState, useEffect, useRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import DragDrop from '@editorjs/drag-drop';
import Undo from "@editorjs/undo";
import editorTools, { convertDataType, updateLinkEvents } from './EditorTools';

const Editor = (props) => {
    const editorInstance = useRef(null);
    const editorContainer = useRef(null);
    const isInitializing = useRef(true);

    useEffect(() => {  
        if(props.docId === "" || !props.editorData) return;
        if (editorInstance.current) {
            editorInstance.current.destroy();
            editorInstance.current = null;
        }
        if(editorContainer.current) {
            isInitializing.current = true;
            const editor = new EditorJS({
                readOnly: props.ViewMode,
                holder: editorContainer.current,
                minHeight: props.folderName === "" ? 0 : 100,
                placeholder: !props.ViewMode ? '입력해주세요.' : '',
                autofocus: !props.ViewMode,
            
                inlineToolbar: ['convertTo', 'bold', 'italic', 'underline', 'textColor', 'marker', 'inlineCode', 'link', 'textAlign', 'emoji', 'linklist'],

                tools: editorTools(props.folderName, props.docId),
    
                data: {},
                onReady: async () => {
                    editorInstance.current = editor;
                    document.addEventListener('paste', handlePaste);
                    const scrollContainer = document.querySelector('.editor-container');
                    if(scrollContainer) {
                        scrollContainer.addEventListener('focusin', (event) => {
                            ensureFocusInViewOptimized(event.target);
                        });
                    }
                    try {
                        loadEditorData(props.editorData.data);
                        const undo = new Undo({
                            editor: editor,
                            config: {
                                debounceTimer: 0,
                                maxLength: 1000000,
                                shortcuts: {
                                    undo: "CMD+Z",
                                    redo: "CMD+SHIFT+Z"
                                }
                            }
                        });
                        undo.initialize(props.editorData?.data || {});
                        new DragDrop(editorInstance.current);
                    } catch (e) {
                        console.error("EditorJS initialization error:", e);
                    }
                },
                onChange: async (api, event) => {
                    if (isInitializing.current) {
                        isInitializing.current = false;
                        return;
                    }
                    if(!props.ViewMode) {
                        await checkRemoveEvent(api, event);
                        const result = await saveEditorData(api);
                        if(result) props.setEditorData(prev => { return {...prev, data: result}});
                    }
                },
                onDestroy: () => {}
            });
            if(props.setEditor) props.setEditor(editor);
        }

        return () => {
            if (editorInstance.current) {
                editorInstance.current.destroy();
                editorInstance.current = null;
                document.removeEventListener('paste', handlePaste);
            }
        }
    }, [props.docId, props.ViewMode]);

    const loadEditorData = async (data) => {
        if(data && data.blocks) {
            await editorInstance.current.render(convertDataType(data));
        }
        else {
            await editorInstance.current.clear();
        }
        updateLinkEvents(props.ViewMode);
    }
    
    useEffect(() => {
        if(editorInstance.current) {
            if(!props.ViewMode && editorInstance.current.configuration) {
                editorInstance.current.configuration.tools.linklist.config.list = props.editorData.relationInfo || [];
            }
            if(props.ViewMode) {
                loadEditorData(props.editorData.data);
            }
        }
    }, [props.editorData]);

    const ensureFocusInView = (target) => {
        if(!editorInstance.current || !target) return;
        const index = editorInstance.current.blocks.getCurrentBlockIndex();
        const length = editorInstance.current.blocks.getBlocksCount();
        if(index < length - 2) return;
        const block = editorInstance.current.blocks.getBlockByIndex(index)?.holder;
        if(!block) return;
        const scrollContainer = document.querySelector('.editor-container');
        let offsetTop = block.offsetTop || 0;
        if(!target.className.includes('cdx-block')) {
            const selection = window.getSelection();
            if (!selection.rangeCount || selection.anchorNode.nodeType === Node.TEXT_NODE) return;

            const range = selection.getRangeAt(0);
            let rangeTop = 0;
            rangeTop += range.endContainer.offsetParent ? range.endContainer.offsetParent.offsetTop : 0;
            rangeTop += range.endContainer.offsetTop
            offsetTop += rangeTop;
        }
        if (offsetTop === 0) return;
        scrollContainer.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }

    let scrollTimeout;
    const ensureFocusInViewOptimized = (target) => {
        if (scrollTimeout) return;
        scrollTimeout = setTimeout(() => {
            ensureFocusInView(target);
            scrollTimeout = null;
        }, 50);
    }

    const saveEditorData = async (editor) => {
        try {
            const savedData = await editor.saver.save();
            let isValid = true;

            // 블록 데이터를 순회하며 유효성 검사 수행
            for (const block of savedData.blocks) {
                const blockType = block.type;   
                const blockData = block.data;   
                let message = '';
        
                // 블록 타입에 따른 유효성 검사
                switch (blockType) {
                    case 'header':
                        block.type = 'h1';
                        isValid = blockData.text && blockData.text.trim() !== '';
                        message = !isValid ? "헤더 내용을 입력해주세요." : "";
                        break;
                    case 'h1':
                    case 'h2':
                    case 'h3':
                    case 'h4':
                        isValid = blockData.text && blockData.text.trim() !== '';
                        message = !isValid ? "헤더 내용을 입력해주세요." : "";
                        break;
                    case 'image':
                        const isUrl = blockData.file.url && blockData.file.url.trim() !== "";
                        const isDesc = blockData.caption && blockData.caption.trim() !== '';
                        isValid = isUrl && isDesc;
                        message = !isUrl ? "이미지 정보가 존재하지 않습니다." : !isDesc ? "이미지 설명은 필수 입력 항목입니다." : "";
                        break;
                    case 'table':
                        isValid = blockData.title && blockData.title.trim() !== '';
                        message = !isValid ? "테이블 제목은 필수 입력 항목입니다." : "";
                        break;
                    default:
                        isValid = true;
                }
                props.setValid({isValid: isValid, message: message});
                if (!isValid) {
                    return; // 유효하지 않은 블록이 있으면 중단
                }
            }
            return savedData;
        } catch (error) {
            console.error('저장 중 오류 발생:', error);
        }
    };

    const checkRemoveEvent = async (api, event) => {
        let removedEvent = null;
        if (Array.isArray(event)) {
            removedEvent = event.find(e => e.type === "block-removed");
        } else {
            if(event.type === "block-removed") {
                removedEvent = event;
            }
        }
        if(!removedEvent || (removedEvent.detail.target.name !== "image")) return;
        let removedPath = "";
        const parser = new DOMParser();
        const doc = parser.parseFromString(removedEvent.detail.target.holder.innerHTML, 'text/html');

        if (removedEvent.detail.target.name === "image") {
            removedPath = { filePath: doc.querySelector('[src]').getAttribute('src').split("doc/")[1] };
        }       
        props.setRemoveData(prev => {return [...prev, removedPath]});
    }

    const handlePaste = async (e) => {
        const types = e.clipboardData?.types;
        if (!types || (!types.includes('text/plain') && types.includes('Files'))) return;
        const htmlData = e.clipboardData?.getData('text/html');
        const files = extractFilesFromHTML(htmlData);
        // 파일 복사하는 api 호출
        if(!files.length) return;
        const response = await fetch('api/Edit/CopyFilesByPath', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(files)
        });
        const result = await response.json();
        if (response.status === 200 && result.length > 0) {
            // 복사된 경로로 이미지 src를 동적으로 교체
            setTimeout(async () => {
                result.forEach(path => {
                    const imgEl = findImgElement(path); // 원본 경로로 찾기
                    if (imgEl) imgEl.src = path; // 복사된 경로로 변경
                });
                // 포커스 위치 저장
                const currentBlockIndex = editorInstance.current.blocks.getCurrentBlockIndex();
                // EditorJS 데이터 동기화
                const data = await editorInstance.current.save();
                await editorInstance.current.render(data);

                const restoreFocus = () => {
                    const index = editorInstance.current.blocks.getBlocksCount() - 1 || currentBlockIndex;
                    const block = editorInstance.current.blocks.getBlockByIndex(index)?.holder;
                    if(!block) return;
                    const scrollContainer = document.querySelector('.editor-container');
                    let offsetTop = block.offsetTop || 0;
                    const selection = window.getSelection();
                    if (!selection.rangeCount || selection.anchorNode.nodeType === Node.TEXT_NODE) return;

                    const range = selection.getRangeAt(0);
                    let rangeTop = 0;
                    rangeTop += range.endContainer.offsetParent ? range.endContainer.offsetParent.offsetTop : 0;
                    rangeTop += range.endContainer.offsetTop
                    offsetTop += rangeTop;
                    if (offsetTop === 0) return;
                    scrollContainer.scrollTo({ top: offsetTop, behavior: 'smooth' });
                };
                setTimeout(async () => {restoreFocus()}, 100);
            }, 100); // 붙여넣기 후 약간의 딜레이 필요
        }
    }

    const findImgElement = (targetSrc) => {
        const images = document.querySelectorAll('img');
        for (const img of images) {
            if (img.src === targetSrc || img.getAttribute('src') === targetSrc) {
            return img;
            }
        }
    }

    const extractFilesFromHTML = (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const imgs = doc.querySelectorAll('img');
        const imgPaths = Array.from(imgs).map(img => {
            return { 
                filePath: img.src.split('doc/')[1], 
                targetPath: `${props.folderName}/${props.docId}/` 
            }
        });
        
        // 추후 첨부파일도 추가
        return imgPaths;
    }

    return (
        <div id="editorjs" className='editor' ref={editorContainer} style={{width: '800px'}}></div>
    )
}
export default Editor;